import { action, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { manageEach, moveItem } from '@/features/legends/editor/collections'
import type { SymbolContext } from '@/features/legends/editor/entities'
import { validateElements } from '@/utils/validx-util'
import {
  InfoLineEditorViewModel,
  type InfoLineTab,
} from './InfoLineEditorViewModel'
import type { InfoLine } from './legend-options'

/**
 * The legend's info lines, with the selected one.
 */
export class InfoLinesBuilderViewModel extends LegendPartViewModelBase {
  private readonly _context: SymbolContext
  private readonly _schema: IValidationSchema<InfoLinesBuilderViewModel> = {
    lines: [validateElements('There are invalid lines.')],
  }

  readonly lines = observable.array<InfoLineEditorViewModel>([], {
    deep: false,
  })
  @observable.ref accessor selectedLineVm: InfoLineEditorViewModel | null = null

  /**
   * The inspector tab, kept while switching lines.
   */
  @observable accessor inspectorTab: InfoLineTab = 'General'

  readonly validation = validationContext(this, this._schema)

  /**
   * @param dependencies
   * @param context The builder the lines belong to; their formulas see the
   *   symbols declared before it.
   */
  constructor(dependencies: LegendPartDependencies, context: SymbolContext) {
    super(dependencies)
    this._context = context
    this.lifecycle.manage(manageEach(this.lifecycle, () => this.lines))
  }

  /**
   * Adds an empty line and selects it.
   */
  @action.bound
  add() {
    const lineVm = this._createLine()
    this.lines.push(lineVm)
    this.edit(lineVm)
  }

  /**
   * Selects the line for the inspector, or clears the selection.
   */
  @action.bound
  edit(lineVm: InfoLineEditorViewModel | null) {
    this.selectedLineVm = lineVm && this.lines.includes(lineVm) ? lineVm : null
  }

  /**
   * Removes the line, and its selection.
   */
  @action.bound
  remove(lineVm: InfoLineEditorViewModel) {
    this.lines.remove(lineVm)
    if (this.selectedLineVm === lineVm) {
      this.edit(null)
    }
  }

  /**
   * Moves a line, as dragging it does.
   */
  @action.bound
  moveByIndex(fromIndex: number, toIndex: number) {
    moveItem(this.lines, fromIndex, toIndex)
  }

  /**
   * Shows the inspector tab.
   */
  @action.bound
  setInspectorTab(tab: InfoLineTab) {
    this.inspectorTab = tab
  }

  /**
   * Sets the lines from the legend document.
   */
  @action
  hydrate(lines: InfoLine[]) {
    this.lines.replace(
      lines.map((data) => {
        const lineVm = this._createLine()
        lineVm.hydrate(data)
        return lineVm
      }),
    )
  }

  /**
   * Shows the analyzer's errors on the lines.
   */
  receiveErrors(descriptor: ErrorDescriptor | null | undefined) {
    addGenericErrorForProperty(
      this,
      descriptor,
      'lines',
      'There are invalid info lines',
    )
    receiveErrors(this.lines, descriptor?.inner)
  }

  /**
   * The lines as the legend document has them.
   */
  toJSON(): InfoLine[] {
    return this.lines.map((l) => l.toJSON())
  }

  private _createLine() {
    return new InfoLineEditorViewModel(this.dependencies, this._context)
  }
}
